import { normalizeName } from '../shared/naming';
import type { UnitOfMeasureId } from '../shared/ids';

/**
 * Unidade de medida.
 *
 * `decimals` não é enfeite: é ele que decide o arredondamento da quantidade (RN-020).
 * Saco de cimento não se compra pela metade — `sc` tem 0 casas —, enquanto concreto se
 * mede em metro cúbico com três. Guardar a quantidade sem respeitar isso produz pedido
 * de compra impossível de atender.
 */
export interface UnitOfMeasure {
  readonly id: UnitOfMeasureId;
  readonly code: string;
  readonly name: string;
  readonly decimals: number;
  readonly isActive: boolean;
}

/** Chave natural: código, comparado sem diferenciar maiúsculas (RN-011 por analogia). */
export function unitNaturalKey(code: string): string {
  return normalizeName(code);
}

/**
 * Arredonda pela precisão da unidade.
 *
 * Usa a aritmética inteira do domínio em vez de `toFixed`: `0.1 + 0.2` em ponto
 * flutuante é o erro clássico, e quantidade de material vira ordem de compra.
 */
export function roundQuantity(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/** `"1.250,5 m²"` — quantidade com a unidade, na precisão certa. */
export function formatQuantity(value: number, unit: UnitOfMeasure): string {
  const rounded = roundQuantity(value, unit.decimals);
  const text = rounded.toLocaleString('pt-BR', {
    minimumFractionDigits: unit.decimals,
    maximumFractionDigits: unit.decimals,
  });
  return `${text} ${unit.name}`;
}

/**
 * Catálogo semeado no bootstrap.
 *
 * O `name` é o que aparece na tela ao lado da quantidade; o `code` é o que casa com a
 * planilha na importação da Fase 3.
 */
export const DEFAULT_UNITS: readonly { code: string; name: string; decimals: number }[] = [
  { code: 'un', name: 'un', decimals: 0 },
  { code: 'sc', name: 'sc', decimals: 0 },
  { code: 'm', name: 'm', decimals: 2 },
  { code: 'm2', name: 'm²', decimals: 2 },
  { code: 'm3', name: 'm³', decimals: 3 },
  { code: 'kg', name: 'kg', decimals: 2 },
  { code: 't', name: 't', decimals: 3 },
  { code: 'L', name: 'L', decimals: 2 },
  { code: 'cj', name: 'cj', decimals: 0 },
  { code: 'vb', name: 'vb', decimals: 2 },
  // Homem-hora: a unidade da mão de obra no quantitativo (RN-166). Duas casas porque
  // meia hora de servente é meia hora, e arredondar para cima inventa jornada.
  { code: 'hh', name: 'HH', decimals: 2 },
  // As do orçamento que o catálogo não tinha (Fase 26, `0052`). Inteiras: ninguém compra
  // meio rolo de manta; a fração do rateio por pavimento é provisória e guarda as casas do
  // rateio, não as da unidade (RN-345).
  { code: 'rolo', name: 'rolo', decimals: 0 },
  { code: 'tb', name: 'tb', decimals: 0 },
  { code: 'lista', name: 'lista', decimals: 0 },
  // Os terceirizados do orçamento contratados por tempo ou por ponto (Fase 26). A hora não
  // é o homem-hora: é a hora de máquina do empreiteiro de retroescavadeira. A diária é a do
  // topógrafo e do ensaio PDA; o ponto, o de gás e o do PSCIP. Hora e diária aceitam
  // fração (meia diária existe); ponto, não.
  { code: 'h', name: 'h', decimals: 2 },
  { code: 'diaria', name: 'diária', decimals: 2 },
  { code: 'pt', name: 'pt', decimals: 0 },
];

/** O código da unidade em que a mão de obra é medida (RN-166). */
export const MAN_HOUR_UNIT_CODE = 'hh';

/** O código da unidade de área — a do serviço que se mede em pano (RN-379). */
export const AREA_UNIT_CODE = 'm2';

/**
 * A unidade é de área?
 *
 * Alvenaria, chapisco, emboço, reboco, gesso e drywall são medidos em m², e é a unidade —
 * não o nome do serviço — que diz se a produção pode ser calculada pela medida
 * (altura × largura, RN-379). Um serviço novo em m² ganha a conta sem cadastro nenhum; um
 * em unidade ou m³ nunca a oferece, porque ela não faz sentido para ele.
 */
export function isAreaUnitCode(code: string | undefined): boolean {
  return code !== undefined && unitNaturalKey(code) === AREA_UNIT_CODE;
}

/**
 * O nome por extenso das unidades semeadas, para a tela que escolhe uma.
 *
 * O `name` do catálogo é a sigla — é o que cabe ao lado da quantidade numa linha da
 * árvore. Numa lista de opções, porém, "cj", "vb" e "HH" sozinhos são charada: quem
 * escolhe a medida de uma disciplina precisa ler "Conjunto", "Verba", "Homem-hora".
 * Unidade criada depois, sem entrada aqui, aparece pela sigla mesmo.
 */
const UNIT_LONG_NAME: Readonly<Record<string, string>> = {
  un: 'Unidade',
  sc: 'Saco',
  m: 'Metro',
  m2: 'Metro quadrado',
  m3: 'Metro cúbico',
  kg: 'Quilograma',
  t: 'Tonelada',
  L: 'Litro',
  cj: 'Conjunto',
  vb: 'Verba',
  hh: 'Homem-hora',
  rolo: 'Rolo',
  tb: 'Tubo',
  lista: 'Lista de material',
  h: 'Hora',
  diaria: 'Diária',
  pt: 'Ponto',
};

/** `"Metro quadrado (m²)"` — ou só a sigla, quando não há nome conhecido. */
export function describeUnit(unit: Pick<UnitOfMeasure, 'code' | 'name'>): string {
  const longName = UNIT_LONG_NAME[unit.code];
  return longName === undefined ? unit.name : `${longName} (${unit.name})`;
}
